import { Component, Input } from '@angular/core';

export interface CoachingMonthlyTarget {
  year: number;
  month: number;   // 1 = January ... 12 = December
  target: number;
}

// Monthly coaching targets of a business unit or a job: one number per calendar month (year + month).
// Edits the given array IN PLACE (the parent form sends it as-is on save). An empty month = no target for that month.
@Component({
  selector: 'crm-coaching-monthly-targets',
  templateUrl: './coaching-monthly-targets.component.html'
})
export class CoachingMonthlyTargetsComponent {
  @Input() targets: CoachingMonthlyTarget[];
  @Input() hint: string;

  readonly monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  readonly months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  year: number = new Date().getFullYear();
  fillValue: number;

  get years(): number[] {
    const now = new Date().getFullYear();
    const set = new Set<number>([now - 1, now, now + 1, this.year]);
    (this.targets || []).forEach(t => set.add(t.year));
    return Array.from(set).sort((a, b) => a - b);
  }

  valueOf(month: number): number {
    const t = (this.targets || []).find(x => x.year === this.year && x.month === month);
    return t ? t.target : null;
  }

  setValue(month: number, value: any, year: number = this.year) {
    if (!this.targets) return;
    const i = this.targets.findIndex(x => x.year === year && x.month === month);
    const n = (value === null || value === undefined || value === '') ? null : Math.max(0, Math.floor(Number(value)));
    if (n === null || isNaN(n)) {
      if (i > -1) this.targets.splice(i, 1);           // empty = no target for that month
    } else if (i > -1) {
      this.targets[i].target = n;
    } else {
      this.targets.push({ year: year, month: month, target: n });
    }
  }

  yearTotal(): number {
    return (this.targets || []).filter(x => x.year === this.year).reduce((s, x) => s + x.target, 0);
  }

  fillAll() {
    if (this.fillValue === null || this.fillValue === undefined || <any>this.fillValue === '') return;
    this.months.forEach(m => this.setValue(m, this.fillValue));
  }

  copyPreviousYear() {
    const prev = (this.targets || []).filter(x => x.year === this.year - 1);
    this.months.forEach(m => {
      const p = prev.find(x => x.month === m);
      this.setValue(m, p ? p.target : null);
    });
  }

  clearYear() {
    this.months.forEach(m => this.setValue(m, null));
  }
}
